import React, { useState, useRef } from "react";
import { Upload, Loader2, AlertCircle, CheckCircle2, Ban, ChevronRight, Users } from "lucide-react";
import { ParseLog } from "../types";

interface UploadViewProps {
  onUpload: (file: File) => void;
  isProcessing: boolean;
  logs: ParseLog[];
  hasData: boolean;
  onViewChart: () => void;
}

const UploadView: React.FC<UploadViewProps> = ({ onUpload, isProcessing, logs, hasData, onViewChart }) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      onUpload(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      onUpload(e.target.files[0]);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
      <div className="text-center space-y-4">
         <h2 className="text-4xl font-bold bg-gradient-to-r from-slate-800 via-blue-700 to-indigo-700 bg-clip-text text-transparent">排班数据上传</h2>
         <p className="text-slate-600 text-lg">支持 <span className="font-semibold text-blue-600">.xlsx / .xls</span> 格式，自动识别 <span className="font-semibold text-indigo-600">21天/15天/闲鱼/转介绍</span> 等排班表</p>
      </div>

      <div 
        className={`group relative border-3 border-dashed rounded-3xl p-16 text-center transition-all duration-300 cursor-pointer backdrop-blur-sm ${isDragging ? 'border-blue-500 bg-gradient-to-br from-blue-50 to-indigo-50 scale-[1.03] shadow-2xl shadow-blue-200/50' : 'border-slate-300 bg-white/60 hover:border-blue-400 hover:bg-gradient-to-br hover:from-blue-50/50 hover:to-indigo-50/50 hover:shadow-xl hover:scale-[1.01]'}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
         <input type="file" ref={fileInputRef} onChange={handleFileChange} className="hidden" accept=".xlsx,.xls" />
         <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-blue-400/5 to-indigo-400/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
         <div className="relative flex flex-col items-center gap-6">
            <div className={`relative w-20 h-20 rounded-2xl flex items-center justify-center transition-all duration-500 ${isProcessing ? 'bg-gradient-to-br from-blue-400 to-indigo-500 text-white shadow-lg shadow-blue-400/50 animate-pulse' : 'bg-gradient-to-br from-slate-100 to-slate-200 text-slate-500 group-hover:from-blue-100 group-hover:to-indigo-100 group-hover:text-blue-600 group-hover:scale-110 group-hover:rotate-3 group-hover:shadow-xl'}`}>
               {isProcessing ? (
                 <Loader2 className="animate-spin" size={36} />
               ) : (
                 <>
                   <Upload size={36} className="transition-transform duration-300 group-hover:scale-110" />
                   <div className="absolute -inset-1 rounded-2xl bg-gradient-to-br from-blue-400 to-indigo-500 opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-500"></div>
                 </>
               )}
            </div>
            <div className="space-y-2">
               <p className="text-xl font-bold text-slate-800">
                 {isProcessing ? (
                   <span className="flex items-center gap-2 justify-center">
                     <span className="inline-block w-2 h-2 bg-blue-500 rounded-full animate-ping"></span>
                     正在解析数据...
                   </span>
                 ) : (
                   <span className="group-hover:text-blue-700 transition-colors">点击或拖拽文件至此</span>
                 )}
               </p>
               <p className="text-sm text-slate-500 group-hover:text-slate-600 transition-colors">支持多Sheet自动合并识别 • 自动过滤历史数据</p>
            </div>
         </div>
      </div>

      {logs.length > 0 && (
        <div className="backdrop-blur-sm bg-white/80 rounded-2xl border border-slate-200/60 overflow-hidden shadow-xl shadow-slate-200/50 animate-in slide-in-from-bottom-2 duration-500">
           <div className="px-6 py-5 border-b border-slate-200/60 bg-gradient-to-r from-slate-50 to-blue-50/50 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-1 h-8 bg-gradient-to-b from-blue-500 to-indigo-500 rounded-full"></div>
                <h3 className="font-bold text-lg text-slate-800">解析日志</h3>
                <span className="text-xs text-slate-500 bg-slate-200/60 px-2 py-1 rounded-full">{logs.length} 个表格</span>
              </div>
              {hasData && (
                <button 
                  onClick={onViewChart} 
                  className="group relative px-5 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white text-sm font-bold rounded-xl transition-all duration-300 flex items-center gap-2 shadow-lg shadow-blue-500/30 hover:shadow-xl hover:shadow-blue-500/40 hover:scale-105"
                >
                   <span>查看排班图</span>
                   <ChevronRight size={16} className="transition-transform group-hover:translate-x-1" />
                   <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </button>
              )}
           </div>
           <div className="divide-y divide-slate-100/80 max-h-80 overflow-y-auto">
              {logs.map((log, idx) => (
                 <div 
                   key={idx} 
                   className="px-6 py-4 flex items-center justify-between text-sm hover:bg-gradient-to-r hover:from-blue-50/30 hover:to-indigo-50/30 transition-all duration-200 group"
                 >
                    <div className="flex items-center gap-4">
                       <div className="flex-shrink-0">
                         {log.status === 'success' && (
                           <div className="w-9 h-9 rounded-xl bg-emerald-100 flex items-center justify-center group-hover:scale-110 transition-transform">
                             <CheckCircle2 size={18} className="text-emerald-600" />
                           </div>
                         )}
                         {log.status === 'skipped' && (
                           <div className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center group-hover:scale-110 transition-transform">
                             <AlertCircle size={18} className="text-slate-500" />
                           </div>
                         )}
                         {log.status === 'error' && (
                           <div className="w-9 h-9 rounded-xl bg-red-100 flex items-center justify-center group-hover:scale-110 transition-transform">
                             <Ban size={18} className="text-red-600" />
                           </div>
                         )}
                       </div>
                       <span className={`font-semibold ${log.status === 'success' ? 'text-slate-800' : 'text-slate-600'}`}>{log.sheetName}</span>
                    </div>
                    <div className="flex items-center gap-4">
                       <span className="text-slate-600">{log.message}</span>
                       {log.count > 0 && (
                         <span className="bg-gradient-to-r from-emerald-400 to-green-500 text-white px-3 py-1 rounded-lg text-xs font-bold shadow-sm group-hover:shadow-md group-hover:scale-110 transition-all">
                           {log.count} 条
                         </span>
                       )}
                    </div>
                 </div>
              ))}
           </div>
        </div>
      )}
    </div>
  );
};

export default UploadView;
